import { isRecord } from '@destera/core';
import type { AnyLocale, Locale } from './locales.ts';
import { createTranslator, type MessageMap, type Translate } from './translator.ts';

/**
 * Bir ad alanının (widget, panel, site…) dil dosyalarını yükleyen katalog. Dosyalar
 * `import.meta.glob` ile toplanır:
 * - tembel modda her dil ayrı parçadır ve YALNIZ o dil iner (başka dil yedek olarak indirilmez);
 * - yeni dil dosyası eklemek için kod değişmez (SOLID-O).
 * Eksik dosya ya da anahtar çalışma zamanında değil, CI'daki katalog testinde yakalanır.
 */
type Loader = () => Promise<unknown>;

export type Catalog<Source extends MessageMap, L extends AnyLocale = Locale> = {
  readonly namespace: string;
  load(locale: L): Promise<Translate<Source>>;
};

const isLoader = (file: unknown): file is Loader => typeof file === 'function';

function localeFromPath(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1).replace(/\.json$/, '');
}

/** Dil dosyasının içeriğini doğrular (yalnız metin değerler); modül sarmalayıcısını açar. */
export function parseMessages(module: unknown, where: string): MessageMap {
  const value = isRecord(module) && 'default' in module ? module['default'] : module;
  if (!isRecord(value)) throw new Error(`Invalid message file: ${where}`);
  const entries = Object.entries(value);
  if (!entries.every((entry): entry is [string, string] => typeof entry[1] === 'string')) {
    throw new Error(`Invalid message file (non-string value): ${where}`);
  }
  return Object.fromEntries(entries);
}

export function defineCatalog<Source extends MessageMap, L extends AnyLocale = Locale>(
  namespace: string,
  files: Readonly<Record<string, unknown>>,
): Catalog<Source, L> {
  const byLocale = new Map(
    Object.entries(files).map(([path, file]) => [localeFromPath(path), file]),
  );

  return {
    namespace,
    load: async (locale) => {
      const where = `${namespace}/${locale}`;
      const file = byLocale.get(locale);
      if (file === undefined) throw new Error(`Missing message file: ${where}`);
      const module = isLoader(file) ? await file() : file;
      return createTranslator<Source>(locale, parseMessages(module, where));
    },
  };
}
